import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import { Box, Button, ButtonBase, Chip, IconButton, Stack, Typography } from '@mui/material';
import React from 'react';
import { IVerbaScholaLessonGroup, IVerbaScholaStudentProgress, IVerbaScholaSummary } from '../api/VerbaScholaApi';
import { VerbaScholaEmptyState } from '../component/VerbaScholaTeacherShell';
import { BLUE, BLUE_HOVER, BORDER, GREEN, INTER, NAVY, ON_NAVY_ACCENT, ON_NAVY_TEXT, POPPINS, SLATE, SOFT_BLUE, SUCCESS_SOFT, SUCCESS_TEXT, SURFACE_MUTED, SURFACE_SUBTLE, WARNING_SOFT, WARNING_TEXT } from '../style/verbaScholaStyles';
import { lessonSummaryLine } from '../util/lessonSummary';

function percentLabel(value: number | null | undefined): string {
    if (value === null) return '-';
    return typeof value === 'number' && Number.isFinite(value) ? `${Math.round(value)}%` : '—';
}

function pluralize(count: number, singular: string, plural = `${singular}s`): string {
    return `${count} ${count === 1 ? singular : plural}`;
}

function statusChipSx(status: string) {
    const positive = status === 'accepted' || status === 'published' || status === 'current' || status === 'owner';
    return {
        fontFamily: INTER,
        fontWeight: 600,
        fontSize: 11,
        textTransform: 'capitalize',
        color: positive ? SUCCESS_TEXT : WARNING_TEXT,
        backgroundColor: positive ? SUCCESS_SOFT : WARNING_SOFT,
    };
}

const METRIC_CARD_SX = { minWidth: 0, backgroundColor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '20px', p: 2.25, boxShadow: '0 4px 12px rgba(22, 58, 99, 0.08)' };

function OverviewMetricCard({ value, label, note, icon: Icon, color, background, onOpen }: { value: string; label: string; note: string; icon: React.ElementType; color: string; background: string; onOpen?: () => void }) {
    const content = (
        <>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 24, lineHeight: 1.1, color: NAVY }}>{value}</Typography>
                    <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12.5, color: NAVY, mt: 0.65 }}>{label}</Typography>
                </Box>
                <Box sx={{ width: 40, height: 40, ml: 1, flexShrink: 0, borderRadius: '12px', display: 'grid', placeItems: 'center', color, backgroundColor: background }}>
                    <Icon sx={{ fontSize: 21 }} />
                </Box>
            </Stack>
            <Typography sx={{ fontFamily: INTER, fontSize: 11.5, color: SLATE, mt: 1.35 }}>{note}</Typography>
        </>
    );

    if (!onOpen) return <Box sx={METRIC_CARD_SX}>{content}</Box>;

    return (
        <ButtonBase
            onClick={onOpen}
            aria-label={`${label}: ${value}`}
            sx={{
                ...METRIC_CARD_SX,
                display: 'block',
                textAlign: 'left',
                cursor: 'pointer',
                '&.Mui-focusVisible': { outline: `2px solid ${BLUE}`, outlineOffset: 2 },
            }}
        >
            {content}
        </ButtonBase>
    );
}

function ReviewQueueMetric({ count, disabled, onOpen }: { count: number; disabled: boolean; onOpen: () => void }) {
    return (
        <Button
            onClick={onOpen}
            disabled={disabled}
            aria-label="Open speaking review queue"
            sx={{
                minWidth: 0,
                p: 2.25,
                borderRadius: '20px',
                justifyContent: 'flex-start',
                textAlign: 'left',
                textTransform: 'none',
                color: '#fff',
                backgroundColor: NAVY,
                boxShadow: '0 4px 12px rgba(22, 58, 99, 0.08)',
                '&:hover': { backgroundColor: NAVY, filter: 'brightness(1.08)' },
                '&.Mui-disabled': { color: ON_NAVY_TEXT, backgroundColor: NAVY, opacity: 0.88 },
            }}
        >
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 24, lineHeight: 1.1, color: '#fff' }}>{count}</Typography>
                <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12.5, color: '#fff', mt: 0.65 }}>Recordings to review</Typography>
                <Typography sx={{ fontFamily: INTER, fontSize: 11.5, color: ON_NAVY_TEXT, mt: 1.35 }}>{disabled ? 'No lesson review queue yet' : 'Open review queue ->'}</Typography>
            </Box>
            <Box sx={{ width: 40, height: 40, ml: 1, flexShrink: 0, borderRadius: '12px', display: 'grid', placeItems: 'center', color: ON_NAVY_ACCENT, backgroundColor: 'rgba(255,255,255,0.1)' }}>
                <RecordVoiceOverRoundedIcon sx={{ fontSize: 21 }} />
            </Box>
        </Button>
    );
}

function InsightTile({ title, value, note, color = NAVY }: { title: string; value: string; note: string; color?: string }) {
    return (
        <Box sx={{ minWidth: 0, p: 1.75, borderRadius: '14px', backgroundColor: SURFACE_SUBTLE }}>
            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12, color: NAVY }}>{title}</Typography>
            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12, color, mt: 0.75 }}>{value}</Typography>
            <Typography sx={{ fontFamily: INTER, fontSize: 11.5, color: SLATE, mt: 0.3 }}>{note}</Typography>
        </Box>
    );
}

function LessonMetricTile({ value, label, note }: { value: string; label: string; note: string }) {
    return (
        <Box sx={{ minWidth: 0, p: 1.5, borderRadius: '14px', backgroundColor: SURFACE_SUBTLE }}>
            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 18, lineHeight: 1, color: NAVY }}>{value}</Typography>
            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, color: NAVY, mt: 0.75 }}>{label}</Typography>
            <Typography sx={{ fontFamily: INTER, fontSize: 11, lineHeight: 1.4, color: SLATE, mt: 0.4 }}>{note}</Typography>
        </Box>
    );
}

function percentNumber(value: number | null | undefined): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

const STUDENT_VISIBLE_LESSON_STATUSES = new Set(['current', 'published', 'complete']);

function lessonSortValue(group: IVerbaScholaLessonGroup): number {
    return group.lesson.order_index ?? Number.MIN_SAFE_INTEGER;
}

function currentLessonGroup(groups: IVerbaScholaLessonGroup[]): IVerbaScholaLessonGroup | null {
    return [...groups]
        .filter(group => STUDENT_VISIBLE_LESSON_STATUSES.has(group.lesson.status) && !group.lesson.is_hidden && !group.lesson.is_archived)
        .sort((a, b) => {
            const orderDiff = lessonSortValue(b) - lessonSortValue(a);
            return orderDiff !== 0 ? orderDiff : b.lesson.id - a.lesson.id;
        })[0] ?? null;
}

function completedStudentCount(students: IVerbaScholaStudentProgress[]): number {
    return students.filter(student => percentNumber(student.completion_percent) >= 100).length;
}

function insightGroups(groups: IVerbaScholaLessonGroup[]) {
    if (groups.length === 0) return [];

    const attempted = groups.filter(group => group.summary.attempt_count > 0);
    const strongest = attempted
        .filter(group => group.summary.accuracy_percent != null)
        .sort((a, b) => percentNumber(b.summary.accuracy_percent) - percentNumber(a.summary.accuracy_percent))[0];
    const mostActive = attempted.sort((a, b) => b.summary.attempt_count - a.summary.attempt_count)[0];
    const needsPractice = attempted
        .filter(group => group.summary.accuracy_percent != null)
        .sort((a, b) => percentNumber(a.summary.accuracy_percent) - percentNumber(b.summary.accuracy_percent))[0];

    return [
        strongest && {
            title: 'Strongest lesson',
            value: `${percentLabel(strongest.summary.accuracy_percent)} accuracy`,
            note: strongest.lesson.title,
            color: SUCCESS_TEXT,
        },
        needsPractice && needsPractice.lesson.id !== strongest?.lesson.id && {
            title: 'Needs practice',
            value: `${percentLabel(needsPractice.summary.accuracy_percent)} accuracy`,
            note: needsPractice.lesson.title,
            color: WARNING_TEXT,
        },
        mostActive && {
            title: 'Most active lesson',
            value: `${pluralize(mostActive.summary.attempt_count, 'attempt')}`,
            note: mostActive.lesson.title,
            color: BLUE,
        },
    ].filter(Boolean).slice(0, 3) as { title: string; value: string; note: string; color: string }[];
}

function OverviewMetrics({ summary, reviewDisabled, onOpenReview, onOpenTab }: { summary: IVerbaScholaSummary; reviewDisabled: boolean; onOpenReview: () => void; onOpenTab: (tab: CourseOverviewLinkTab) => void }) {
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' }, gap: 2 }}>
            <OverviewMetricCard value={String(summary.student_count)} label="Students" note="Enrolled in this class" icon={GroupsRoundedIcon} color={BLUE} background={SOFT_BLUE} onOpen={() => onOpenTab('student')} />
            <OverviewMetricCard value={String(summary.lesson_count ?? 0)} label="Lessons" note="In this class" icon={MenuBookRoundedIcon} color={GREEN} background={SUCCESS_SOFT} onOpen={() => onOpenTab('lesson')} />
            <OverviewMetricCard value={percentLabel(summary.completion_percent)} label="Lesson completion" note="Started activities count toward progress" icon={CheckCircleRoundedIcon} color={GREEN} background={SUCCESS_SOFT} />
            <OverviewMetricCard value={percentLabel(summary.accuracy_percent)} label="Answer accuracy" note="Best result for each activity" icon={QueryStatsRoundedIcon} color={BLUE} background={SOFT_BLUE} />
            <OverviewMetricCard value={String(summary.attempt_count)} label="Attempts" note="Across all class lessons" icon={RecordVoiceOverRoundedIcon} color={NAVY} background={SURFACE_MUTED} />
            <ReviewQueueMetric count={summary.awaiting_review_count ?? 0} disabled={reviewDisabled} onOpen={onOpenReview} />
        </Box>
    );
}

function ExerciseInsights({ groups }: { groups: IVerbaScholaLessonGroup[] }) {
    const insights = insightGroups(groups);

    return (
        <Box sx={{ backgroundColor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '20px', p: 2.5 }}>
            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 17, color: NAVY }}>Lesson insights</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2, mt: 2 }}>
                {insights.length === 0 ? (
                    <InsightTile title="Lesson activity" value="No attempts yet" note="Insights appear after students practice." color={SLATE} />
                ) : (
                    insights.map(item => <InsightTile key={`${item.title}-${item.note}`} title={item.title} value={item.value} note={item.note} color={item.color} />)
                )}
            </Box>
        </Box>
    );
}

function CurrentLessonCard({ courseName, group, studentCount, onOpen }: { courseName: string; group: IVerbaScholaLessonGroup | null; studentCount: number; onOpen: () => void }) {
    const lesson = group?.lesson;
    const summary = group?.summary;
    const completedCount = completedStudentCount(group?.students ?? []);
    const stillWorking = Math.max(studentCount - completedCount, 0);

    return (
        <Box sx={{ backgroundColor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '20px', overflow: 'hidden' }}>
            <Stack direction="row" alignItems="center" sx={{ p: 2.5, borderBottom: `1px solid ${BORDER}` }}>
                <Box>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 17, color: NAVY }}>Current lesson</Typography>
                    <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 0.25 }}>In progress for {courseName}</Typography>
                </Box>
                {lesson && <Chip label={lesson.status} size="small" sx={{ ml: 'auto', ...statusChipSx(lesson.status) }} />}
                {lesson && (
                    <IconButton aria-label={`Open ${lesson.title}`} onClick={onOpen} sx={{ ml: 1, color: BLUE, backgroundColor: SOFT_BLUE, '&:hover': { backgroundColor: BLUE_HOVER } }}>
                        <ChevronRightRoundedIcon />
                    </IconButton>
                )}
            </Stack>
            {lesson && summary ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.05fr) minmax(420px, .95fr)' }, gap: 3, alignItems: 'center', p: 2.5 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.25}>
                        <Box sx={{ width: { xs: '100%', sm: 84 }, height: 84, flexShrink: 0, borderRadius: '18px', display: 'grid', placeItems: 'center', color: BLUE, backgroundColor: SOFT_BLUE }}>
                            <MenuBookRoundedIcon sx={{ fontSize: 34 }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 18, color: NAVY }}>{lesson.title}</Typography>
                            <Typography sx={{ fontFamily: INTER, fontSize: 12.5, lineHeight: 1.55, color: SLATE, mt: 0.5 }}>
                                {lessonSummaryLine(lesson) || 'Students work through the lesson exercises and speaking practice.'}
                            </Typography>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                                <Chip label={lesson.status} size="small" sx={{ fontFamily: INTER, fontSize: 11.5, backgroundColor: SURFACE_MUTED, color: SLATE }} />
                                <Chip label={pluralize(summary.attempt_count, 'attempt')} size="small" sx={{ fontFamily: INTER, fontSize: 11.5, backgroundColor: SURFACE_MUTED, color: SLATE }} />
                            </Stack>
                        </Box>
                    </Stack>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.25 }}>
                        <LessonMetricTile
                            value={`${completedCount}/${studentCount}`}
                            label="Students complete"
                            note={studentCount === 0 ? 'No students enrolled yet' : stillWorking > 0 ? `${stillWorking} still working` : 'All enrolled students complete'}
                        />
                        <LessonMetricTile value={percentLabel(summary.accuracy_percent)} label="Answer accuracy" note="Best result for each activity" />
                        <LessonMetricTile value={String(summary.attempt_count)} label="Attempts" note="Across lesson exercises" />
                    </Box>
                </Box>
            ) : (
                <VerbaScholaEmptyState message="This class has no lessons yet." />
            )}
        </Box>
    );
}

export type CourseOverviewLinkTab = 'lesson' | 'student';

interface Props {
    courseName: string;
    summary: IVerbaScholaSummary;
    groups: IVerbaScholaLessonGroup[];
    onOpenLesson: (lessonId: number) => void;
    onOpenReview: (lessonId: number) => void;
    onOpenTab: (tab: CourseOverviewLinkTab) => void;
}

export default function VerbaScholaTeacherCourseOverviewPage({ courseName, summary, groups, onOpenLesson, onOpenReview, onOpenTab }: Props) {
    const currentLesson = currentLessonGroup(groups);
    const reviewLesson = groups.find(group => (group.summary.awaiting_review_count ?? 0) > 0) ?? currentLesson;
    return (
        <Stack spacing={2.5}>
            <OverviewMetrics summary={summary} reviewDisabled={!currentLesson} onOpenReview={() => { if (reviewLesson) onOpenReview(reviewLesson.lesson.id); }} onOpenTab={onOpenTab} />
            <ExerciseInsights groups={groups} />
            <CurrentLessonCard courseName={courseName} group={currentLesson} studentCount={summary.student_count} onOpen={() => { if (currentLesson) onOpenLesson(currentLesson.lesson.id); }} />
        </Stack>
    );
}
